import z from "zod";
import { labelSchema } from "../label";
import { paginatedResponseSchema, paginationQuerySchema, timestampsSchema } from "../helpers";
import { visionConversationSummarySchema } from "../vision/vision.schema";
import { autoAnnotationSchema } from "../autoAnnotation/auto-annotation.schema";
export var TaskStatusEnum;
(function (TaskStatusEnum) {
    TaskStatusEnum["TODO"] = "TODO";
    TaskStatusEnum["DONE"] = "DONE";
})(TaskStatusEnum || (TaskStatusEnum = {}));
export var TaskFileTypeEnum;
(function (TaskFileTypeEnum) {
    TaskFileTypeEnum["GS"] = "GS";
})(TaskFileTypeEnum || (TaskFileTypeEnum = {}));
export const rectangleAnnotationSchema = z.object({
    id: z.number(),
    label: labelSchema,
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    groupId: z.string().uuid().nullish(),
});
export const createRectangleAnnotationSchema = z.object({
    id: z.number().int().positive().optional(),
    labelId: z.number(),
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    groupId: z.string().uuid().nullish(),
});
export const polygonAnnotationSchema = z.object({
    id: z.number(),
    label: labelSchema,
    value: z.tuple([z.number(), z.number()]).array(),
    groupId: z.string().uuid().nullish(),
});
export const createPolygonAnnotationSchema = z.object({
    id: z.number().int().positive().optional(),
    labelId: z.number(),
    value: z.tuple([z.number(), z.number()]).array(),
    groupId: z.string().uuid().nullish(),
});
export const classificationAnnotationSchema = z.object({
    id: z.number(),
    label: labelSchema,
});
export const createClassificationAnnotationSchema = z.object({
    id: z.number().int().positive().optional(),
    labelId: z.number()
});
export const taskSortBySchema = z.enum([
    "createdAt",
    "updatedAt",
    "meanConfidence",
    "meanIou",
    "precision",
    "recall",
]);
export const taskSchema = z.object({
    id: z.number(),
    iid: z.string(),
    fileType: z.enum(TaskFileTypeEnum),
    filePath: z.string(),
    thumbnailUrl: z.string(),
    width: z.number(),
    height: z.number(),
    status: z.enum(TaskStatusEnum),
    annotationCount: z.number().nullable(),
    updatedBy: z.number().nullable().optional(),
    meanConfidence: z.number().nullable().optional(),
    meanIou: z.number().nullable().optional(),
    precision: z.number().nullable().optional(),
    recall: z.number().nullable().optional(),
    sourceDashboardId: z.number().nullable().optional(),
    sourceEventId: z.number().nullable().optional(),
    sourceTaskId: z.number().nullable().optional(),
    awaitingReview: z.boolean().optional(),
    running: z.boolean().optional(),
    ...timestampsSchema
});
export const taskDetailSchema = taskSchema.extend({
    rectangleAnnotations: rectangleAnnotationSchema.array().nullable(),
    polygonAnnotations: polygonAnnotationSchema.array().nullable(),
    classificationAnnotations: classificationAnnotationSchema.array().nullable(),
    visionConversation: visionConversationSummarySchema.nullable(),
    visionTurnStartedAt: z.iso.datetime().nullable(),
    autoAnnotations: autoAnnotationSchema.array(),
});
export const updateStagedAnnotationSchema = z.object({
    id: z.number().int().positive(),
    labelId: z.number(),
    x: z.number().optional(),
    y: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    value: z.tuple([z.number(), z.number()]).array().optional(),
});
export const updateTaskSchema = z.object({
    rectangleAnnotations: createRectangleAnnotationSchema.array().nullish(),
    polygonAnnotations: createPolygonAnnotationSchema.array().nullish(),
    classificationAnnotations: createClassificationAnnotationSchema.array().nullish(),
    reviewed: z.boolean().optional(),
    approve: z.boolean().optional(),
    stagedAnnotations: updateStagedAnnotationSchema.array().optional(),
});
export const taskPaginationQuerySchema = paginationQuerySchema.extend({
    deleted: z
        .union([z.literal("true"), z.literal("false"), z.literal("null")])
        .optional()
        .transform((val) => {
        if (val === "true")
            return true;
        if (val === "null")
            return null;
        return false;
    }),
    annotated: z
        .union([z.literal("true"), z.literal("false")])
        .optional()
        .transform((val) => {
        if (val === "true")
            return true;
        if (val === "false")
            return false;
        return undefined;
    }),
    labelIds: z
        .string()
        .optional()
        .transform((val) => {
        if (!val)
            return undefined;
        return val.split(",").map(Number).filter((n) => !isNaN(n));
    }),
    ids: z
        .string()
        .optional()
        .transform((val) => {
        if (!val)
            return undefined;
        return val.split(",").map(Number).filter((n) => !isNaN(n));
    }),
    updatedBy: z
        .string()
        .optional()
        .transform((val) => {
        if (!val)
            return undefined;
        return val.split(",").map(Number).filter((n) => !isNaN(n));
    }),
    awaitingReview: z
        .union([z.literal("true"), z.literal("false")])
        .optional()
        .transform((val) => {
        if (val === "true")
            return true;
        if (val === "false")
            return false;
        return undefined;
    }),
    sortBy: taskSortBySchema.optional().default("createdAt"),
    sortOrder: z
        .union([z.literal("asc"), z.literal("desc")])
        .optional()
        .default("desc"),
});
export const paginatedTaskSchema = paginatedResponseSchema(taskSchema);
export const taskUploadContentTypeSchema = z.enum([
    "image/jpeg",
    "image/png",
    "image/webp",
]);
export const requestTaskUploadSchema = z.object({
    capturedAt: z.iso.datetime().optional(),
    contentType: taskUploadContentTypeSchema.default("image/jpeg"),
});
export const taskUploadUrlSchema = z.object({
    pendingTaskId: z.number(),
    uploadUrl: z.string(),
    objectPath: z.string(),
});
export const confirmTaskUploadSchema = z.object({
    pendingTaskId: z.number(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    sourceDashboardId: z.number().int().positive().optional(),
    sourceEventId: z.number().int().positive().optional(),
});
export const importTasksSchema = z.object({
    sourceProjectId: z.number().int().positive(),
    taskIds: z.number().int().positive().array().min(1).max(100),
});
export const importTasksResponseSchema = z.object({
    imported: taskSchema.array(),
    failed: z
        .object({
        sourceTaskId: z.number(),
        reason: z.string(),
    })
        .array(),
});
export const importedSourceTaskIdsQuerySchema = z.object({
    sourceProjectId: z.coerce.number().int().positive(),
});
export const importedSourceTaskIdsResponseSchema = z.object({
    taskIds: z.number().array(),
});
export const importedEventsQuerySchema = z.object({
    dashboardId: z.coerce.number().int().positive(),
    eventIds: z
        .string()
        .transform((val) => val.split(",").map(Number).filter((n) => !isNaN(n))),
});
export const importedEventIdsResponseSchema = z.object({
    eventIds: z.number().array(),
});
export const taskExportRectangleAnnotationSchema = z.object({
    id: z.number(),
    labelId: z.number(),
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    groupId: z.string().uuid().nullish(),
});
export const taskExportPolygonAnnotationSchema = z.object({
    id: z.number(),
    labelId: z.number(),
    value: z.tuple([z.number(), z.number()]).array(),
    groupId: z.string().uuid().nullish(),
});
export const taskExportClassificationAnnotationSchema = z.object({
    id: z.number(),
    labelId: z.number(),
});
export const taskExportItemSchema = z.object({
    id: z.number(),
    iid: z.string(),
    filePath: z.string(),
    width: z.number(),
    height: z.number(),
    status: z.enum(TaskStatusEnum),
    createdAt: z.iso.datetime(),
    rectangleAnnotations: taskExportRectangleAnnotationSchema.array(),
    polygonAnnotations: taskExportPolygonAnnotationSchema.array(),
    classificationAnnotations: taskExportClassificationAnnotationSchema.array(),
});
export const taskExportSchema = z.object({
    project: z.object({
        id: z.number(),
        name: z.string(),
    }),
    exportedAt: z.iso.datetime(),
    labels: z
        .object({
        id: z.number(),
        name: z.string(),
        color: z.string(),
    })
        .array(),
    tasks: taskExportItemSchema.array(),
});
export const taskExportQuerySchema = z.object({
    annotated: z
        .union([z.literal("true"), z.literal("false")])
        .optional()
        .transform((val) => {
        if (val === "true")
            return true;
        if (val === "false")
            return false;
        return undefined;
    }),
    labelIds: z
        .string()
        .optional()
        .transform((val) => {
        if (!val)
            return undefined;
        return val.split(",").map(Number).filter((n) => !isNaN(n));
    }),
});
export const taskIdsQuerySchema = z.object({
    annotated: z
        .union([z.literal("true"), z.literal("false")])
        .optional()
        .transform((val) => {
        if (val === "true")
            return true;
        if (val === "false")
            return false;
        return undefined;
    }),
    labelIds: z
        .string()
        .optional()
        .transform((val) => {
        if (!val)
            return undefined;
        return val.split(",").map(Number).filter((n) => !isNaN(n));
    }),
    sortBy: taskSortBySchema.optional().default("createdAt"),
    sortOrder: z
        .union([z.literal("asc"), z.literal("desc")])
        .optional()
        .default("desc"),
});
export const taskIdsResponseSchema = z.object({
    ids: z.number().array(),
});
export const annotationHistoryActionSchema = z.enum(["created", "updated"]);
const historyUserSchema = z.object({
    id: z.number(),
    fullName: z.string(),
}).nullable();
export const rectangleHistorySnapshotSchema = z.object({
    labelId: z.number(),
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    groupId: z.string().uuid().nullish(),
});
export const polygonHistorySnapshotSchema = z.object({
    labelId: z.number(),
    value: z.tuple([z.number(), z.number()]).array(),
    groupId: z.string().uuid().nullish(),
});
export const classificationHistorySnapshotSchema = z.object({
    labelId: z.number(),
});
const historyEventBaseSchema = z.object({
    id: z.number(),
    action: annotationHistoryActionSchema,
    createdAt: z.iso.datetime(),
    user: historyUserSchema,
});
export const rectangleHistoryEventSchema = historyEventBaseSchema.extend({
    snapshot: rectangleHistorySnapshotSchema,
});
export const polygonHistoryEventSchema = historyEventBaseSchema.extend({
    snapshot: polygonHistorySnapshotSchema,
});
export const classificationHistoryEventSchema = historyEventBaseSchema.extend({
    snapshot: classificationHistorySnapshotSchema,
});
export const rectangleAnnotationHistoryGroupSchema = z.object({
    annotationId: z.number(),
    events: rectangleHistoryEventSchema.array(),
});
export const polygonAnnotationHistoryGroupSchema = z.object({
    annotationId: z.number(),
    events: polygonHistoryEventSchema.array(),
});
export const classificationAnnotationHistoryGroupSchema = z.object({
    annotationId: z.number(),
    events: classificationHistoryEventSchema.array(),
});
export const taskHistoryResponseSchema = z.object({
    rectangleHistory: rectangleAnnotationHistoryGroupSchema.array(),
    polygonHistory: polygonAnnotationHistoryGroupSchema.array(),
    classificationHistory: classificationAnnotationHistoryGroupSchema.array(),
});
//# sourceMappingURL=task.schema.js.map